import { useEffect, useState } from "react";
import { Loader2, UserPlus, Film, Gift, ShoppingCart, DollarSign } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CARDS = [
  ["signups", "Vrais inscrits", UserPlus, "#2f7bff"],
  ["clips", "Clips créés", Film, "#a855f7"],
  ["won", "Clips gagnés (codes/cadeaux)", Gift, "#22e3a1"],
  ["bought", "Clips achetés", ShoppingCart, "#ffc94d"],
  ["revenue", "Ventes ($ CAD)", DollarSign, "#ff4d6d"],
];
const RANGES = [7, 30, 90];

const GameStats = ({ game }) => {
  const total = game.reduce((a, g) => a + g.count, 0);
  return (
    <div className="cx-panel rounded-2xl p-5" data-testid="real-stats-game">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm text-white font-semibold">🎴 Jeu de cartes LANCEMENT</h4>
        <span className="text-xs text-slate-400" data-testid="real-stats-game-total">{total} parties</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {game.map((g) => (
          <div key={g.prize} className="rounded-xl bg-black/30 border border-white/5 p-4" data-testid={`real-stats-game-${g.prize}`}>
            <div className="text-xs text-slate-400">Carte {g.prize} clips</div>
            <div className="text-2xl font-bold text-white font-mono mt-1">{g.count}</div>
            <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2f7bff] to-[#a855f7]" style={{ width: `${total ? (g.count / total) * 100 : 0}%` }} />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{total ? Math.round((g.count / total) * 100) : 0} %</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RealStatsTab = () => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/admin/real-stats?days=${days}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [days]);

  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const data = d.series.map((r) => ({ ...r, label: r.day.slice(5) }));
  return (
    <div className="space-y-6" data-testid="real-stats-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-lg text-white">Vrais chiffres</h3>
          <p className="text-sm text-slate-400">Seulement les vrais comptes ({d.real_users}) : sans tests, sans démo, sans ton espace admin.</p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((n) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`real-stats-range-${n}`} aria-pressed={days === n}
              className={`cx-metal rounded-full px-4 py-1.5 text-xs ${days === n ? "cx-metal-active" : ""}`}>{n} jours</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CARDS.map(([k, label, Icon, color]) => (
          <div key={k} className="cx-panel rounded-2xl p-4" data-testid={`real-stats-total-${k}`}>
            <Icon className="w-4 h-4" style={{ color }} />
            <div className="text-2xl font-bold text-white font-mono mt-2">{k === "revenue" ? d.total[k].toFixed(2) : d.total[k]}</div>
            <div className="text-xs text-slate-400 mt-1">{label}</div>
          </div>
        ))}
      </div>
      <GameStats game={d.game} />
      <div className="cx-panel rounded-2xl p-4 h-80" data-testid="real-stats-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0b0f1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {CARDS.slice(0, 4).map(([k, label, , color]) => <Bar key={k} dataKey={k} name={label} fill={color} radius={[4, 4, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
